import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Target, Zap, Flame } from 'lucide-react';
import { DIFFICULTY_CONFIG } from './PacManChaseBar';

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 80px;
  z-index: 40;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(4px);
  padding: 2rem;
`;

const Panel = styled(motion.div)`
  background: rgba(255, 255, 255, 0.97);
  border-radius: 20px;
  padding: 2.5rem;
  max-width: 520px;
  width: 100%;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  text-align: center;
`;

const Title = styled.h2`
  font-size: 1.6rem;
  font-weight: 700;
  color: #333;
  margin: 0 0 0.5rem;
`;

const Subtitle = styled.p`
  color: #666;
  font-size: 0.95rem;
  line-height: 1.5;
  margin: 0 0 2rem;
`;

const OptionsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const DifficultyButton = styled(motion.button)`
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 1rem 1.25rem;
  border: 2px solid ${props => props.$borderColor};
  border-radius: 14px;
  background: ${props => props.$bg};
  cursor: pointer;
  text-align: left;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
  }
`;

const IconBox = styled.div`
  width: 44px;
  height: 44px;
  border-radius: 12px;
  background: ${props => props.$color};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: white;
`;

const OptionText = styled.div`
  flex: 1;
`;

const OptionLabel = styled.div`
  font-weight: 700;
  font-size: 1rem;
  color: #333;
  margin-bottom: 2px;
`;

const OptionDesc = styled.div`
  font-size: 0.8rem;
  color: #666;
`;

const DIFFICULTY_STYLES = {
  easy: { icon: Target, color: '#22c55e', bg: 'rgba(34, 197, 94, 0.08)', border: '#22c55e' },
  medium: { icon: Zap, color: '#f59e0b', bg: 'rgba(245, 158, 11, 0.08)', border: '#f59e0b' },
  hard: { icon: Flame, color: '#ef4444', bg: 'rgba(239, 68, 68, 0.08)', border: '#ef4444' },
};

const DifficultySelect = ({ onSelect }) => {
  return (
    <Overlay
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.3 }}
    >
      <Panel
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ duration: 0.4, delay: 0.1 }}
      >
        <Title>Choose Your Challenge</Title>
        <Subtitle>
          Answer correctly to fill the progress bar and move your avatar toward the door.
          Wrong answers let Pac-Man advance toward you — but only until you pass the door.
          Reach your difficulty target (60%, 70%, or 85% correct) to escape!
        </Subtitle>
        <OptionsGrid>
          {Object.entries(DIFFICULTY_CONFIG).map(([key, config]) => {
            const style = DIFFICULTY_STYLES[key];
            const Icon = style.icon;
            return (
              <DifficultyButton
                key={key}
                $bg={style.bg}
                $borderColor={style.border}
                onClick={() => onSelect(key)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <IconBox $color={style.color}>
                  <Icon size={22} />
                </IconBox>
                <OptionText>
                  <OptionLabel>{config.label}</OptionLabel>
                  <OptionDesc>{config.description}</OptionDesc>
                </OptionText>
              </DifficultyButton>
            );
          })}
        </OptionsGrid>
      </Panel>
    </Overlay>
  );
};

export default DifficultySelect;
